import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, RefreshCw, AlertTriangle, Check, Globe } from 'lucide-react';
import { api } from '../utils/api';

interface APIKey {
  id: string;
  org_id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  rate_limit: number;
  created_at: string;
  expires_at?: string;
  last_used?: string;
  is_active: boolean;
}

interface Webhook {
  id: string;
  org_id: string;
  url: string;
  events: string[];
  is_active: boolean;
  created_at: string;
  last_triggered?: string;
  failure_count: number;
}

interface APISettingsProps {
  orgId: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PERMISSIONS = [
  { id: 'read:reports', label: 'Read Reports', description: 'View claim data and reports' },
  { id: 'read:pdf', label: 'Read PDFs', description: 'Download PDF reports' },
  { id: 'write:reports', label: 'Write Reports', description: 'Create and update reports' },
  { id: 'manage:shares', label: 'Manage Shares', description: 'Create/revoke share links' },
  { id: 'admin', label: 'Admin', description: 'Full access (trusted partners)' }
];

const WEBHOOK_EVENTS = [
  { id: 'claim.created', label: 'Claim Created' },
  { id: 'claim.updated', label: 'Claim Updated' },
  { id: 'claim.calculated', label: 'Claim Calculated' },
  { id: 'settlement.updated', label: 'Settlement Updated' }
];

export default function APISettings({ orgId }: APISettingsProps) {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Create key form
  const [keyName, setKeyName] = useState('');
  const [keyPermissions, setKeyPermissions] = useState<string[]>(['read:reports']);
  const [keyRateLimit, setKeyRateLimit] = useState(1000);
  const [keyExpiresDays, setKeyExpiresDays] = useState(365);

  // Create webhook form
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      // Note: In real implementation, you'd use an admin API key
      const [keysData, webhooksData] = await Promise.all([
        api.get(`/api/v1/external/keys?org_id=${orgId}`, { showErrorToast: false }),
        api.get('/api/v1/external/webhooks', {
          headers: { 'X-API-Key': 'admin-key' }, // Would use actual key
          showErrorToast: false
        })
      ]);

      setApiKeys(keysData || []);
      setWebhooks(webhooksData || []);
    } catch (error) {
      console.error('Error fetching API data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const data = await api.post('/api/v1/external/keys', {
        org_id: orgId,
        name: keyName,
        permissions: keyPermissions,
        rate_limit: keyRateLimit,
        expires_in_days: keyExpiresDays
      });

      setNewKeySecret(data.api_key);
      setApiKeys([...apiKeys, data]);
      setShowCreateKey(false);
      setKeyName('');
      setKeyPermissions(['read:reports']);
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      const data = await api.post('/api/v1/external/webhooks',
        { url: webhookUrl, events: webhookEvents },
        { headers: { 'X-API-Key': 'admin-key' } } // Would use actual key
      );

      setWebhooks([...webhooks, data]);
      setShowCreateWebhook(false);
      setWebhookUrl('');
      setWebhookEvents([]);
    } catch (error) {
      console.error('Error creating webhook:', error);
    }
  };

  const handleCopyKey = () => {
    if (newKeySecret) {
      navigator.clipboard.writeText(newKeySecret);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key?')) return;

    try {
      await api.delete(`/api/v1/external/keys/${keyId}`, {
        headers: { 'X-API-Key': 'admin-key' }
      });
      setApiKeys(apiKeys.filter(k => k.id !== keyId));
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await api.delete(`/api/v1/external/webhooks/${webhookId}`, {
        headers: { 'X-API-Key': 'admin-key' }
      });
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
    } catch (error) {
      console.error('Error deleting webhook:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New Key Secret Modal */}
      {newKeySecret && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">API Key Created!</h3>
                <p className="text-sm text-gray-500">Save this key now - it won't be shown again</p>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <code className="text-green-400 text-sm break-all">{newKeySecret}</code>
                <button
                  onClick={handleCopyKey}
                  className="ml-2 p-2 hover:bg-gray-700 rounded transition"
                >
                  {copiedKey ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Store this key securely</span>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                This is the only time you'll see this key. If you lose it, you'll need to create a new one.
              </p>
            </div>

            <button
              onClick={() => setNewKeySecret(null)}
              className="w-full py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800"
            >
              I've saved my key
            </button>
          </div>
        </div>
      )}

      {/* API Keys Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">API Keys</h2>
                <p className="text-sm text-gray-300">Manage programmatic access to your data</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateKey(true)}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Key
            </button>
          </div>
        </div>

        <div className="p-6">
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Key className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No API keys created yet</p>
              <p className="text-sm mt-1">Create a key to start using the API</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map(key => (
                <div 
                  key={key.id}
                  className={`p-4 border rounded-lg ${key.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{key.name}</span>
                        {!key.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Revoked</span>
                        )}
                      </div>
                      <code className="text-sm text-gray-500">{key.key_prefix}••••••••</code>
                    </div>
                    {key.is_active && (
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {key.permissions.map(p => (
                      <span key={p} className="px-2 py-1 bg-gray-100 rounded">{p}</span>
                    ))}
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {key.rate_limit}/hour
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    Created {formatDate(key.created_at)}
                    {key.last_used && ` • Last used ${formatDate(key.last_used)}`}
                    {key.expires_at && ` • Expires ${formatDate(key.expires_at)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Key Form */}
        {showCreateKey && (
          <div className="border-t p-6 bg-gray-50">
            <h3 className="font-semibold mb-4">Create New API Key</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name *</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., Production Server"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_PERMISSIONS.map(perm => (
                    <label 
                      key={perm.id}
                      className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${
                        keyPermissions.includes(perm.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={keyPermissions.includes(perm.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setKeyPermissions([...keyPermissions, perm.id]);
                          } else {
                            setKeyPermissions(keyPermissions.filter(p => p !== perm.id));
                          }
                        }}
                        className="rounded text-blue-600"
                      />
                      <div>
                        <span className="font-medium text-sm">{perm.label}</span>
                        <p className="text-xs text-gray-500">{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (req/hour)</label>
                  <input
                    type="number"
                    value={keyRateLimit}
                    onChange={(e) => setKeyRateLimit(parseInt(e.target.value) || 1000)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires In (days)</label>
                  <input
                    type="number"
                    value={keyExpiresDays}
                    onChange={(e) => setKeyExpiresDays(parseInt(e.target.value) || 365)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateKey(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={!keyName}
                  className="flex-1 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
                >
                  Create Key
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Webhooks Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Webhooks</h2>
                <p className="text-sm text-emerald-100">Receive real-time notifications</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateWebhook(true)}
              className="px-4 py-2 bg-white text-emerald-700 rounded-lg font-semibold hover:bg-emerald-50 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Webhook
            </button>
          </div>
        </div>

        <div className="p-6">
          {webhooks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No webhooks configured</p>
              <p className="text-sm mt-1">Add a webhook to receive event notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map(webhook => (
                <div 
                  key={webhook.id}
                  className={`p-4 border rounded-lg ${webhook.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <code className="text-sm text-gray-700">{webhook.url}</code>
                      {webhook.failure_count > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                          {webhook.failure_count} failures
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {webhook.events.map(event => (
                      <span key={event} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                        {event}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    Created {formatDate(webhook.created_at)}
                    {webhook.last_triggered && ` • Last triggered ${formatDate(webhook.last_triggered)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Webhook Form */}
        {showCreateWebhook && (
          <div className="border-t p-6 bg-gray-50">
            <h3 className="font-semibold mb-4">Add Webhook</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                <div className="grid grid-cols-2 gap-2">
                  {WEBHOOK_EVENTS.map(event => (
                    <label 
                      key={event.id}
                      className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${
                        webhookEvents.includes(event.id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={webhookEvents.includes(event.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWebhookEvents([...webhookEvents, event.id]);
                          } else {
                            setWebhookEvents(webhookEvents.filter(ev => ev !== event.id));
                          }
                        }}
                        className="rounded text-emerald-600"
                      />
                      <span className="text-sm">{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateWebhook(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWebhook}
                  disabled={!webhookUrl || webhookEvents.length === 0}
                  className="flex-1 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  Add Webhook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
